import { useState } from 'react';
import { showSuccessToast, showErrorToast } from '@lib/toast';
import { Link2, Twitter, Facebook, Linkedin, Mail } from 'lucide-react';

type Props = {
  url: string;
  title?: string | null;
  excerpt?: string | null;
  mobile?: boolean;
};

export default function ShareWidget({
  url,
  title,
  excerpt,
  mobile = false,
}: Props) {
  const [copied, setCopied] = useState(false);

  const shareWindow = (href: string) => {
    try {
      window.open(href, '_blank', 'noopener,noreferrer');
    } catch (err) {
      // ignore
    }
  };

  const fallbackCopy = (text: string) => {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    try {
      document.execCommand('copy');
      document.body.removeChild(el);
      return true;
    } catch {
      document.body.removeChild(el);
      return false;
    }
  };

  const handleCopy = async () => {
    if (!url) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ok = fallbackCopy(url);
        if (!ok) throw new Error('copy-failed');
      }
      setCopied(true);
      showSuccessToast('Copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      showErrorToast('Unable to copy link');
    }
  };

  const shareText = encodeURIComponent(title ?? '');
  const shareSummary = encodeURIComponent(excerpt ?? '');
  const encodedUrl = encodeURIComponent(url);

  if (mobile) {
    return (
      <div className="mt-8 flex items-center justify-center gap-3 md:hidden">
        <div className="relative">
          <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy link"
            title="Copy link"
            className="group flex h-11 w-11 items-center justify-center rounded-lg !bg-indigo-600 !text-white shadow-md transition-colors hover:!bg-indigo-500"
          >
            <Link2 className="h-4 w-4 !text-white" />
          </button>
          {copied && (
            <div className="absolute -top-10 left-1/2 -translate-x-1/2 rounded-md bg-indigo-600 px-3 py-1 text-xs font-semibold text-white shadow">
              Copied!
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() =>
            shareWindow(
              `https://x.com/intent/tweet?url=${encodedUrl}&text=${shareText}`
            )
          }
          aria-label="Share on X.com"
          title="Share on X.com"
          className="flex h-11 w-11 items-center justify-center rounded-lg !bg-indigo-600 !text-white shadow-md hover:!bg-indigo-500"
        >
          <Twitter className="h-4 w-4 !text-white" />
        </button>

        <button
          type="button"
          onClick={() =>
            shareWindow(
              `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
            )
          }
          aria-label="Share on Facebook"
          title="Share on Facebook"
          className="flex h-11 w-11 items-center justify-center rounded-lg !bg-indigo-600 !text-white shadow-md hover:!bg-indigo-500"
        >
          <Facebook className="h-4 w-4 !text-white" />
        </button>

        <button
          type="button"
          onClick={() =>
            shareWindow(
              `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${shareText}&summary=${shareSummary}`
            )
          }
          aria-label="Share on LinkedIn"
          title="Share on LinkedIn"
          className="flex h-11 w-11 items-center justify-center rounded-lg !bg-indigo-600 !text-white shadow-md hover:!bg-indigo-500"
        >
          <Linkedin className="h-4 w-4 !text-white" />
        </button>

        <a
          href={`mailto:?subject=${shareText}&body=${encodedUrl}`}
          rel="noopener noreferrer"
          aria-label="Share by email"
          title="Share by email"
          className="flex h-11 w-11 items-center justify-center rounded-lg !bg-indigo-600 !text-white shadow-md hover:!bg-indigo-500"
        >
          <Mail className="h-4 w-4 !text-white" />
        </a>
      </div>
    );
  }

  return (
    <div className="fixed left-6 top-[140px] z-40 hidden flex-col gap-3 md:flex md:-translate-x-6">
      <div className="relative">
        <button
          type="button"
          onClick={handleCopy}
          aria-label="Copy link"
          title="Copy link"
          className="group flex h-11 w-11 items-center justify-center rounded-lg !bg-indigo-600 !text-white shadow-md transition-colors hover:!bg-indigo-500"
        >
          <Link2 className="h-4 w-4 !text-white" />
        </button>
        {copied && (
          <div className="absolute -left-14 top-1/2 -translate-y-1/2 rounded-md !bg-indigo-600 px-3 py-1 text-xs font-semibold !text-white shadow">
            Copied!
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() =>
          shareWindow(
            `https://x.com/intent/tweet?url=${encodedUrl}&text=${shareText}`
          )
        }
        aria-label="Share on X.com"
        title="Share on X.com"
        className="flex h-11 w-11 items-center justify-center rounded-lg !bg-indigo-600 !text-white shadow-md hover:!bg-indigo-500"
      >
        <Twitter className="h-4 w-4 !text-white" />
      </button>

      <button
        type="button"
        onClick={() =>
          shareWindow(
            `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
          )
        }
        aria-label="Share on Facebook"
        title="Share on Facebook"
        className="flex h-11 w-11 items-center justify-center rounded-lg !bg-indigo-600 !text-white shadow-md hover:!bg-indigo-500"
      >
        <Facebook className="h-4 w-4 !text-white" />
      </button>

      <button
        type="button"
        onClick={() =>
          shareWindow(
            `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${shareText}&summary=${shareSummary}`
          )
        }
        aria-label="Share on LinkedIn"
        title="Share on LinkedIn"
        className="flex h-11 w-11 items-center justify-center rounded-lg !bg-indigo-600 !text-white shadow-md hover:!bg-indigo-500"
      >
        <Linkedin className="h-4 w-4 !text-white" />
      </button>

      <a
        href={`mailto:?subject=${shareText}&body=${encodedUrl}`}
        rel="noopener noreferrer"
        aria-label="Share by email"
        title="Share by email"
        className="flex h-11 w-11 items-center justify-center rounded-lg !bg-indigo-600 !text-white shadow-md hover:!bg-indigo-500"
      >
        <Mail className="h-4 w-4 !text-white" />
      </a>
    </div>
  );
}
